import React, { useState } from "react";
import SEO from "../../../components/SEO/SEO";
import PublicHeader from "../../../components/PublicHeader/PublicHeader";
import PublicFooter from "../../../components/PublicFooter/PublicFooter";

import "./ContactPage.css";

function isValidEmail(v: string): boolean {
  const s = String(v || "").trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
}

function formatPhoneInput(raw: string): string {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 10);
  const a = digits.slice(0, 3);
  const b = digits.slice(3, 6);
  const c = digits.slice(6, 10);

  if (digits.length === 0) return "";
  if (digits.length < 4) return `(${a}`;
  if (digits.length < 7) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
}

type RoleOption = "Patient" | "Family Doctor" | "Clinic Administrator" | "Other";

type ModalState = null | {
  kind: "success" | "error";
  title: string;
  message: string;
};

export default function ContactPage() {
  const [name, setName] = useState<string>("");
  const [role, setRole] = useState<RoleOption | "">("");
  const [otherRole, setOtherRole] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [message, setMessage] = useState<string>("");

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [modal, setModal] = useState<ModalState>(null);

  const [touchedEmail, setTouchedEmail] = useState(false);

  const normalizedEmail = String(email || "").trim().toLowerCase();
  const emailOk = normalizedEmail.length > 0 && isValidEmail(normalizedEmail);

  const canSubmit =
    name.trim().length > 0 &&
    role.length > 0 &&
    (role !== "Other" || otherRole.trim().length > 0) &&
    emailOk &&
    message.trim().length > 0 &&
    !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submitting) return;

    setTouchedEmail(true);

    if (!name.trim()) {
      setModal({
        kind: "error",
        title: "Missing information",
        message: "Please enter your name.",
      });
      return;
    }

    if (!role) {
      setModal({
        kind: "error",
        title: "Missing information",
        message: "Please select your role.",
      });
      return;
    }

    if (role === "Other" && !otherRole.trim()) {
      setModal({
        kind: "error",
        title: "Missing information",
        message: "Please specify your role.",
      });
      return;
    }

    if (!emailOk) {
      setModal({
        kind: "error",
        title: "Invalid email",
        message: "Please enter a valid email address.",
      });
      return;
    }

    if (!message.trim()) {
      setModal({
        kind: "error",
        title: "Missing information",
        message: "Please enter your message.",
      });
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        name: name.trim(),
        role,
        otherRole: role === "Other" ? otherRole.trim() : "",
        email: normalizedEmail,
        phone: phone.trim(),
        message: message.trim(),
      };

      const res = await fetch("/api/contact-form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Request failed (${res.status})`);
      }

      setModal({
        kind: "success",
        title: "Message sent",
        message:
          "Thank you for reaching out. We've received your message and will get back to you soon.",
      });

      // Reset form
      setName("");
      setRole("");
      setOtherRole("");
      setEmail("");
      setPhone("");
      setMessage("");
      setTouchedEmail(false);
    } catch (err: any) {
      setModal({
        kind: "error",
        title: "Could not send message",
        message:
          err?.message ||
          "Something went wrong. Please try again or email us directly.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ocf-page">
      <SEO
        title="Contact Us"
        description="Contact AcceptingMD for technical support with the platform or for clinic and provider inquiries. Patient questions about clinic decisions, appointments, or care should be directed to the clinic."
      />
      <PublicHeader variant="solid" />

      {/* Modal */}
      {modal ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setModal(null)}
          className="contact-modal-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="contact-modal-content"
          >
            <div className="contact-modal-header">
              <div>
                <div className="contact-modal-title">{modal.title}</div>
                <div className="contact-modal-message">{modal.message}</div>
              </div>
              <button
                type="button"
                onClick={() => setModal(null)}
                aria-label="Close"
                className="contact-modal-close"
              >
                &times;
              </button>
            </div>
            <div className="contact-modal-actions">
              <button
                type="button"
                className="contact-btn contact-btn-primary"
                onClick={() => setModal(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Hero */}
      <section className="contact-hero">
        <div className="contact-hero-inner">
          <h1 className="contact-hero-title">Contact Us</h1>
          <p className="contact-hero-subtitle">
            Contact AcceptingMD for platform support, clinic inquiries, or provider assistance.
          </p>
        </div>
      </section>

      <div className="contact-container">
        <div className="contact-card">
          <form className="contact-form" onSubmit={handleSubmit}>
            {/* Name */}
            <div className="contact-field">
              <label className="contact-label">Name *</label>
              <input
                className="contact-input"
                type="text"
                value={name}
                disabled={submitting}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                autoComplete="name"
              />
            </div>

            {/* Role */}
            <div className="contact-field">
              <label className="contact-label">I am a *</label>
              <select
                className="contact-select"
                value={role}
                onChange={(e) => setRole(e.target.value as RoleOption | "")}
                disabled={submitting}
              >
                <option value="" disabled>
                  Select one...
                </option>
                <option value="Patient">Patient — Technical Support Only</option>
                <option value="Family Doctor">Family Doctor</option>
                <option value="Clinic Administrator">Clinic Administrator</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {role === "Patient" ? (
              <div className="contact-patient-warning">
                <strong>Patient technical support only</strong>
                <p>
                  AcceptingMD can only respond to technical issues patients experience while using the platform.
                </p>
                <p>
                  For questions about your request, clinic decision, Meet & Greet, waitlist, appointments, acceptance, attachment, or medical care, please contact the clinic directly. AcceptingMD cannot answer these questions on behalf of the clinic.
                </p>
                <p>
                  Do not include symptoms, medical information, medical records, or other sensitive health information in this form.
                </p>
              </div>
            ) : null}

            {/* Other Role (conditional) */}
            {role === "Other" ? (
              <div className="contact-field">
                <label className="contact-label">Please specify *</label>
                <input
                  className="contact-input"
                  type="text"
                  value={otherRole}
                  disabled={submitting}
                  onChange={(e) => setOtherRole(e.target.value)}
                  placeholder="Your role"
                />
              </div>
            ) : null}

            {/* Email */}
            <div className="contact-field">
              <label className="contact-label">Email *</label>
              <input
                className="contact-input"
                type="email"
                value={email}
                disabled={submitting}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouchedEmail(true)}
                placeholder="you@example.com"
                autoComplete="email"
                aria-invalid={touchedEmail && !emailOk ? true : undefined}
              />
              {touchedEmail && !emailOk ? (
                <div className="contact-helper contact-helper-error">
                  Please enter a valid email address.
                </div>
              ) : null}
            </div>

            {/* Phone (optional) */}
            <div className="contact-field">
              <label className="contact-label">Phone (optional)</label>
              <input
                className="contact-input"
                type="tel"
                value={phone}
                disabled={submitting}
                onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                placeholder="(416) 999-0000"
                inputMode="tel"
                autoComplete="tel"
              />
            </div>

            {/* Message */}
            <div className="contact-field contact-field-full">
              <label className="contact-label">
                {role === "Patient" ? "Describe the technical issue *" : "How can we help? *"}
              </label>
              <textarea
                className="contact-textarea"
                value={message}
                disabled={submitting}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  role === "Patient"
                    ? "Describe the technical problem you are having with the AcceptingMD website or secure link. Do not include medical information."
                    : "Please describe your question or concern..."
                }
                rows={5}
              />
            </div>

            {/* Submit */}
            <div className="contact-actions">
              <button
                type="submit"
                className="contact-btn contact-btn-primary contact-btn-full"
                disabled={!canSubmit}
              >
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </div>
          </form>

          {/* Alternative contact info */}
          <div className="contact-alt">
            <p>
              For technical support or clinic/provider inquiries, you can also reach us at{" "}
              <a href="mailto:support@acceptingmd.ca" className="contact-link">
                support@acceptingmd.ca
              </a>
              . Patient questions about a clinic, request decision, appointment, or medical care must be directed to the clinic.
            </p>
          </div>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
